import { createContext, type ReactNode, type RefObject, useCallback, useContext, useMemo, useRef } from 'react';

import { type DesktopHeaderTrackingData, type DesktopHeaderTrackingLevel } from '../types';

type OnTrackRef = RefObject<((data: DesktopHeaderTrackingData) => void) | undefined>;
type DesktopHeaderTrackingContextValue = { onTrackRef: OnTrackRef };

const DesktopHeaderTrackingContext = createContext<DesktopHeaderTrackingContextValue | null>(null);

type DesktopHeaderTrackingProviderProps = {
  children: ReactNode;
  onTrack?: (data: DesktopHeaderTrackingData) => void;
};

export const DesktopHeaderTrackingProvider = ({ children, onTrack }: DesktopHeaderTrackingProviderProps) => {
  const onTrackRef = useRef(onTrack);
  // Keep track stable while making the latest callback available before effects run.
  onTrackRef.current = onTrack;

  const value = useMemo(() => ({ onTrackRef }), [onTrackRef]);

  return <DesktopHeaderTrackingContext.Provider value={value}>{children}</DesktopHeaderTrackingContext.Provider>;
};

export const useDesktopHeaderTracking = () => {
  const context = useContext(DesktopHeaderTrackingContext);

  return useCallback(
    (level: DesktopHeaderTrackingLevel, selection: string | undefined) => {
      const onTrack = context?.onTrackRef.current;
      if (!onTrack || !selection) return;
      onTrack({ level, selection });
    },
    [context],
  );
};
